/**
 * Analyzes raw authentication methods from Microsoft Graph API
 * and evaluates whether MFA is configured and which tier it represents.
 */

export const MFA_STATUS = {
  AUTHENTICATOR: 'AUTHENTICATOR', // Optimal: MS Authenticator or FIDO2/Passkey
  OTHER_MFA: 'OTHER_MFA',         // Neutral: Legacy methods (SMS / Phone call)
  NO_MFA: 'NO_MFA'                // Action required: No 2FA configured
};

/**
 * Parses and categorizes methods
 * @param {Array} rawMethods - The 'value' array from /v1.0/me/authentication/methods
 */
export function analyzeMfaStatus(rawMethods = []) {
  const parsedMethods = [];
  let hasAuthenticator = false;
  let hasFido = false;
  let hasLegacyPhone = false;
  let hasOtherMfa = false;

  for (const method of rawMethods) {
    const odataType = method['@odata.type'] || '';

    // 1. Microsoft Authenticator (Modern / Preferred)
    if (odataType.includes('microsoftAuthenticatorAuthenticationMethod')) {
      hasAuthenticator = true;
      parsedMethods.push({
        id: method.id || 'authenticator',
        type: 'authenticator',
        nameKey: 'methodAuthenticator',
        detail: method.displayName ? `${method.displayName} ${method.phoneAppVersion ? `(v${method.phoneAppVersion})` : ''}` : null,
        isMfa: true,
        isPreferred: true
      });
      continue;
    }

    // 2. FIDO2 / Passkey (Modern phishing-resistant - counts as Optimal!)
    if (odataType.includes('fido2AuthenticationMethod')) {
      hasFido = true;
      parsedMethods.push({
        id: method.id || 'fido',
        type: 'fido',
        nameKey: 'methodFido',
        detail: method.displayName || method.model || null,
        isMfa: true,
        isPreferred: true // FIDO/Passkey is top tier security
      });
      continue;
    }

    // 3. Phone / SMS (Legacy MFA)
    if (odataType.includes('phoneAuthenticationMethod')) {
      hasLegacyPhone = true;
      hasOtherMfa = true;
      // Mask phone number for privacy: e.g. +49 170 ••• 567
      const maskedNumber = method.phoneNumber 
        ? maskPhoneNumber(method.phoneNumber) 
        : null;

      parsedMethods.push({
        id: method.id || 'phone',
        type: 'phone',
        nameKey: 'methodPhone',
        detail: maskedNumber ? `${maskedNumber} (${method.phoneType || 'mobile'})` : null,
        isMfa: true,
        isPreferred: false,
        isLegacy: true
      });
      continue;
    }

    // 4. Software OATH (e.g. Google Authenticator / 3rd party TOTP)
    if (odataType.includes('softwareOathAuthenticationMethod')) {
      hasOtherMfa = true;
      parsedMethods.push({
        id: method.id || 'softwareOath',
        type: 'softwareOath',
        nameKey: 'methodSoftwareOath',
        detail: null,
        isMfa: true,
        isPreferred: false,
        isLegacy: true
      });
      continue;
    }

    // 5. Windows Hello for Business
    if (odataType.includes('windowsHelloForBusinessAuthenticationMethod')) {
      hasFido = true; // Windows Hello is passkey/FIDO technology
      parsedMethods.push({
        id: method.id || 'windowsHello',
        type: 'windowsHello',
        nameKey: 'methodWindowsHello',
        detail: method.displayName || null,
        isMfa: true,
        isPreferred: true
      });
      continue;
    }

    // 6. Password (Single factor only)
    if (odataType.includes('passwordAuthenticationMethod')) {
      parsedMethods.push({
        id: method.id || 'password',
        type: 'password',
        nameKey: 'methodPassword',
        detail: null,
        isMfa: false,
        isPreferred: false
      });
      continue;
    }

    // Any other authentication method
    if (!odataType.includes('emailAuthenticationMethod')) {
      parsedMethods.push({
        id: method.id || 'other',
        type: 'other',
        nameKey: 'methodOther',
        detail: method.displayName || null,
        isMfa: false,
        isPreferred: false
      });
    }
  }

  // Determine overall status:
  // - If user has Authenticator OR FIDO2/Passkey: OPTIMAL (Green)
  // - If user has only legacy methods (SMS/Phone/TOTP): OTHER_MFA (Neutral)
  // - Otherwise: NO_MFA (Action required / Red)
  let status = MFA_STATUS.NO_MFA;
  if (hasAuthenticator || hasFido) {
    status = MFA_STATUS.AUTHENTICATOR; // Optimal status
  } else if (hasLegacyPhone || hasOtherMfa) {
    status = MFA_STATUS.OTHER_MFA;      // Neutral legacy status
  }

  return {
    status,
    hasAuthenticator,
    hasFido,
    hasLegacyPhone,
    hasOtherMfa,
    methods: parsedMethods,
    mfaCount: parsedMethods.filter(m => m.isMfa).length
  };
}

/**
 * Masks phone numbers to protect personal data
 */
function maskPhoneNumber(phone) {
  if (!phone || phone.length < 6) return phone;
  const start = phone.slice(0, 5);
  const end = phone.slice(-3);
  return `${start} ••• ${end}`;
}

/**
 * Mock datasets for local demo and test mode
 */
export const MOCK_SCENARIOS = {
  authenticator: {
    user: {
      displayName: "Anna Schmidt",
      userPrincipalName: "anna.schmidt@eckes-granini.com",
      mail: "anna.schmidt@eckes-granini.com",
      jobTitle: "Senior Consultant",
      usageLocation: "DE"
    },
    methods: [
      {
        "@odata.type": "#microsoft.graph.passwordAuthenticationMethod",
        id: "pwd-1"
      },
      {
        "@odata.type": "#microsoft.graph.microsoftAuthenticatorAuthenticationMethod",
        id: "auth-1",
        displayName: "iPhone 15 Pro",
        phoneAppVersion: "6.8.12"
      },
      {
        "@odata.type": "#microsoft.graph.phoneAuthenticationMethod",
        id: "phone-1",
        phoneNumber: "+49 170 1234567",
        phoneType: "mobile"
      }
    ]
  },
  other_mfa: {
    user: {
      displayName: "Lukas Weber",
      userPrincipalName: "lukas.weber@eckes-granini.com",
      mail: "lukas.weber@eckes-granini.com",
      jobTitle: "Project Manager",
      usageLocation: "DK"
    },
    methods: [
      {
        "@odata.type": "#microsoft.graph.passwordAuthenticationMethod",
        id: "pwd-2"
      },
      {
        "@odata.type": "#microsoft.graph.phoneAuthenticationMethod",
        id: "phone-2",
        phoneNumber: "+45 20 123456",
        phoneType: "mobile"
      }
    ]
  },
  fido_mfa: {
    user: {
      displayName: "Elena Rostova",
      userPrincipalName: "elena.rostova@eckes-granini.com",
      mail: "elena.rostova@eckes-granini.com",
      jobTitle: "Security Engineer",
      usageLocation: "ES"
    },
    methods: [
      {
        "@odata.type": "#microsoft.graph.passwordAuthenticationMethod",
        id: "pwd-3"
      },
      {
        "@odata.type": "#microsoft.graph.fido2AuthenticationMethod",
        id: "fido-1",
        displayName: "YubiKey 5C NFC",
        model: "YubiKey 5 Series"
      }
    ]
  },
  no_mfa: {
    user: {
      displayName: "Maximilian Koch",
      userPrincipalName: "maximilian.koch@eckes-granini.com",
      mail: "maximilian.koch@eckes-granini.com",
      jobTitle: "Sales Specialist",
      usageLocation: "FR"
    },
    methods: [
      {
        "@odata.type": "#microsoft.graph.passwordAuthenticationMethod",
        id: "pwd-4"
      }
    ]
  }
};
